import { Section } from "@/components/section";
import type { Data } from "@/types/data";
import { HelpCircle, TestTubeDiagonal } from "lucide-react";
import { useMemo, useState } from "react";
import { ScrollArea } from "../ui/scroll-area";
import { ExamItems } from "./exam-items";
import { ExamSearch } from "./exam-search";
import { FaqSection } from "./faq-section";

interface ExamsSectionProps {
	data: Data;
}

export default function ExamsFaqSection({ data }: ExamsSectionProps) {
	const [searchTerm, setSearchTerm] = useState("");

	const filteredExams = useMemo(() => {
		const activeExams = data["area-exames"].exames.filter(
			(exame) => exame.ativo,
		);

		if (!searchTerm.trim()) {
			return activeExams;
		}

		return activeExams.filter(
			(exame) =>
				exame.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
				exame.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
				exame.preparo.toLowerCase().includes(searchTerm.toLowerCase()),
		);
	}, [searchTerm, data]);

	return (
		<Section id="exames-duvidas" className="bg-white">
			<div className="grid grid-cols-1 gap-8 xl:grid-cols-5 xl:items-start">
				<div className="xl:col-span-3">
					<div className="mb-8 text-center xl:text-left">
						<div className="mb-4 flex justify-center xl:justify-start">
							<div className="rounded-full border border-brand-secondary-100 bg-brand-secondary-50 p-3">
								<TestTubeDiagonal className="h-10 w-10 text-brand-secondary-500" />
							</div>
						</div>
						<h2 className="text-3xl font-bold text-brand-700 md:text-4xl">
							{data["area-exames"].titulo}
						</h2>
						<h3 className="mt-2 text-lg text-zinc-600 md:text-xl italic">
							{data["area-exames"].subtitulo}
						</h3>
						<div className="mx-auto mt-4 max-w-3xl space-y-2 text-zinc-600 xl:mx-0">
							<div>
        						{data["area-exames"]["texto-1"].split("\n").map((linha, index) => (
            						<p key={index}>- {linha}</p>
        						))}
   							</div>
							<p>{data["area-exames"]["texto-2"]}</p>
						</div>
					</div>

					<div className="rounded-2xl border border-zinc-200 bg-zinc-100/80 p-4 shadow-sm md:p-5">
						<ExamSearch
							searchTerm={searchTerm}
							onSearchChange={setSearchTerm}
						/>

						<div className="mt-5">
							{filteredExams.length > 0 ? (
								<ScrollArea className="h-[520px] rounded-xl pr-2 md:h-[560px]">
									{filteredExams.map((exame, index) => (
										<ExamItems
											key={`${exame.nome}-${index}`}
											nome={exame.nome}
											preparo={exame.preparo}
											descricao={exame.descricao}
										/>
									))}
								</ScrollArea>
							) : (
								<div className="rounded-xl border border-zinc-200 bg-white p-8 text-center text-zinc-500">
									<p>Nenhum exame encontrado para "{searchTerm}"</p>
								</div>
							)}
						</div>
					</div>
				</div>

				<aside className="xl:col-span-2 xl:sticky xl:top-28">
					<div className="mb-6 text-center">
						<div className="mb-4 flex justify-center">
							<div className="rounded-full border border-brand-secondary-100 bg-brand-secondary-50 p-3">
								<HelpCircle className="h-10 w-10 text-brand-secondary-500" />
							</div>
						</div>
						<h2 className="text-3xl font-bold text-brand-700">
							{data["area-duvidas"].titulo}
						</h2>
						<p className="mt-2 text-zinc-600">
							{data["area-duvidas"].subtitulo}
						</p>
					</div>
					<FaqSection data={data} />
				</aside>
			</div>
		</Section>
	);
}
